import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeError } from '@/lib/safeError';
import { friendlyErrorMessage } from '@/lib/friendlyError';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string)?.replace(/\/$/, '');
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

interface ProjectFile {
  name: string;
  content: string;
}

interface DeployResult {
  success: boolean;
  status: string;
  message?: string;
  deploymentId?: string;
  previewUrl?: string | null;
  inspectUrl?: string;
  error?: string;
  details?: string;
}

type DeployStatus = 'idle' | 'preparing' | 'deploying' | 'success' | 'error';

export type DomainStatus = 'idle' | 'adding' | 'checking' | 'verified' | 'pending' | 'error';

export interface DomainResult {
  success?: boolean;
  domain?: string;
  verified?: boolean;
  configured?: boolean;
  verification?: Array<{ type: string; domain: string; value: string }>;
  error?: string;
}

function deployErrorMessage(status: number, bodyError?: string): string {
  if (status === 401) return 'Session expired, please sign in again';
  if (status === 403) return "You don't own this project";
  if (status === 500) return bodyError || 'Server error. Please try again.';
  return bodyError || `Request failed (${status})`;
}

export function useVercelDeploy() {
  const [status, setStatus] = useState<DeployStatus>('idle');
  const [result, setResult] = useState<DeployResult | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const deploy = useCallback(async (
    projectId: string,
    files: ProjectFile[],
    projectName?: string,
  ): Promise<DeployResult> => {
    setStatus('preparing');
    setResult(null);
    setLogs([]);

    addLog(`Preparing deployment for project: ${projectId}`);
    addLog(`Packaging ${files.length} files...`);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        const err: DeployResult = {
          success: false,
          status: 'error',
          error: 'Session expired, please sign in again',
        };
        setStatus('error');
        setResult(err);
        return err;
      }

      setStatus('deploying');
      addLog('Sending source files to Vercel...');
      addLog('Building your app (this may take 1-2 minutes)...');

      // Send raw source files — edge function transforms to Next.js and Vercel builds
      const deployFiles: ProjectFile[] = files;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/deploy-to-vercel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ projectId, files: deployFiles, projectName }),
      });

      const data = await res.json().catch(() => ({}));
      const bodyError = data?.error ?? data?.message;

      if (!res.ok) {
        const errorMsg = friendlyErrorMessage(deployErrorMessage(res.status, bodyError));
        addLog(`Error: ${errorMsg}`);
        const errorResult: DeployResult = {
          success: false,
          status: 'error',
          error: errorMsg,
        };
        setStatus('error');
        setResult(errorResult);
        return errorResult;
      }

      if (data.success) {
        addLog(`Deployment ${data.status}: ${data.message || 'Success'}`);
        if (data.previewUrl) addLog(`Preview URL: ${data.previewUrl}`);
        if (data.deploymentId) addLog(`Deployment ID: ${data.deploymentId}`);
        setStatus('success');
        setResult(data);
        return data;
      }

      const failMsg = friendlyErrorMessage(bodyError || 'Deployment failed');
      addLog(`Deployment failed: ${failMsg}`);
      const failResult: DeployResult = {
        success: false,
        status: 'error',
        error: failMsg,
        message: data.message,
      };
      setStatus('error');
      setResult(failResult);
      return failResult;
    } catch (err) {
      const e = safeError(err);
      const errMsg = friendlyErrorMessage(e.message);
      addLog(`Error: ${errMsg}`);
      const errorResult: DeployResult = {
        success: false,
        status: 'error',
        error: errMsg,
      };
      setStatus('error');
      setResult(errorResult);
      return errorResult;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setLogs([]);
  }, []);

  // ── Domain management ──

  const [domainStatus, setDomainStatus] = useState<DomainStatus>('idle');
  const [domainResult, setDomainResult] = useState<DomainResult | null>(null);

  const callDomainAction = useCallback(async (
    action: 'add-domain' | 'check-domain',
    projectId: string,
    domain: string,
    projectName?: string,
  ): Promise<DomainResult> => {
    setDomainStatus(action === 'add-domain' ? 'adding' : 'checking');
    setDomainResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        const err: DomainResult = { error: 'Session expired, please sign in again' };
        setDomainStatus('error');
        setDomainResult(err);
        return err;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/deploy-to-vercel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ action, projectId, domain, projectName }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const err: DomainResult = { error: data.error || `Request failed (${res.status})` };
        setDomainStatus('error');
        setDomainResult(err);
        return err;
      }

      const result: DomainResult = {
        success: true,
        domain: data.domain,
        verified: data.verified,
        configured: data.configured,
        verification: data.verification || [],
      };

      setDomainStatus(data.verified || data.configured ? 'verified' : 'pending');
      setDomainResult(result);
      return result;
    } catch (err) {
      const e = safeError(err);
      const errResult: DomainResult = { error: friendlyErrorMessage(e.message) };
      setDomainStatus('error');
      setDomainResult(errResult);
      return errResult;
    }
  }, []);

  const addCustomDomain = useCallback(
    (projectId: string, domain: string, projectName?: string) =>
      callDomainAction('add-domain', projectId, domain, projectName),
    [callDomainAction]
  );

  const checkDomainStatus = useCallback(
    (projectId: string, domain: string, projectName?: string) =>
      callDomainAction('check-domain', projectId, domain, projectName),
    [callDomainAction]
  );

  const resetDomain = useCallback(() => {
    setDomainStatus('idle');
    setDomainResult(null);
  }, []);

  return {
    status,
    isDeploying: status === 'preparing' || status === 'deploying',
    isSuccess: status === 'success',
    isError: status === 'error',
    result,
    logs,
    deploy,
    reset,
    // Domain management
    domainStatus,
    domainResult,
    addCustomDomain,
    checkDomainStatus,
    resetDomain,
  };
}
